import * as z from 'zod';
import type { CampaignVariant, UpdateCampaignInput } from '@/gql/graphql';
import { Channel } from '@/gql/graphql';
import type { Segment } from '@/routes/segments/types';

// Define the structure expected by the form
export type FormSegment = { id: string; name: string; count: number };

// Helper function to calculate total count from channelCounts
export function getTotalSegmentCount(segment: Segment | undefined): number {
  if (!segment) return 0;
  return segment.channelCounts.sms + segment.channelCounts.email + segment.channelCounts.push;
}

// Form schema for audience selection
export const audienceFormSchema = z
  .object({
    segments: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          count: z.number()
        })
      )
      .min(1, 'Select at least one segment'),
    exclusionSegments: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        count: z.number()
      })
    ),
    smartSend: z.boolean().default(false),
    splitConfig: z
      .object({
        isEnabled: z.boolean(),
        splitType: z.enum(['even', 'custom']),
        variants: z.array(
          z.object({
            id: z.string(),
            weight: z.number().nullable(),
            name: z.string()
          })
        )
      })
      .optional()
  })
  .refine(
    (data) => {
      const includedIds = new Set(data.segments.map((s) => s.id));
      const excludedIds = new Set(data.exclusionSegments.map((s) => s.id));
      return ![...includedIds].some((id) => excludedIds.has(id));
    },
    {
      message: 'A segment cannot be both included and excluded',
      path: ['exclusionSegments']
    }
  )
  .refine(
    (data) => {
      if (!data.splitConfig?.isEnabled) return true;
      const totalWeight = data.splitConfig.variants.reduce(
        (sum, variant) => sum + (variant.weight ?? 0),
        0
      );
      const hasEmptyWeights = data.splitConfig.variants.some((v) => v.weight === null);
      return !hasEmptyWeights && totalWeight === 100;
    },
    {
      message: 'Split configuration weights must be valid and sum to 100%',
      path: ['splitConfig']
    }
  );

export type AudienceFormValues = z.infer<typeof audienceFormSchema>;

// Helper function to determine split type from campaign variants
export function determineSplitType(variants: CampaignVariant[]) {
  if (!variants.length) return 'even';
  // If all weights are equal, it's an even split
  const weights = variants.map((v) => v.splitWeight);
  const isEven = weights.every((w) => w === weights[0]);
  return isEven ? 'even' : 'custom';
}

// Helper function to map campaign variants to form state
export function mapVariantsToFormState(variants: CampaignVariant[]) {
  return variants.map((variant) => ({
    id: variant.variantId,
    weight: variant.splitWeight,
    name: variant.variantName
  }));
}

// Helper function to map form state to campaign variants
export function mapFormStateToVariants(
  variants: NonNullable<AudienceFormValues['splitConfig']>['variants']
): UpdateCampaignInput['variants'] {
  return variants.map((variant) => ({
    id: variant.id,
    variantName: variant.name,
    channel: Channel.Sms,
    splitWeight: variant.weight ?? 0,
    content: null
  }));
}

// Map segment IDs to the structure needed by the form state
export function mapSegmentIdsToFormSegments(
  segmentIds: string[],
  availableSegments: Segment[]
): FormSegment[] {
  return segmentIds.map((id) => {
    const segment = availableSegments.find((s) => s.id === id);
    return {
      id,
      name: segment?.name ?? id, // Fallback to ID if name not found (shouldn't happen)
      count: getTotalSegmentCount(segment)
    };
  });
}

// Map Segment objects to the structure needed by MultiSelect options
export function mapSegmentsToMultiSelectOptions(
  segments: Segment[]
): { label: string; value: string }[] {
  return segments.map((segment) => ({
    value: segment.id,
    label: segment.name
  }));
}

// Mock data for segments
export const MOCK_SEGMENTS = [
  { label: 'Loyal Customers', value: 'loyal_customers', count: 5000 },
  { label: 'Recent Purchasers', value: 'recent_purchasers', count: 2500 },
  { label: 'Cart Abandoners', value: 'cart_abandoners', count: 1500 },
  { label: 'Newsletter Subscribers', value: 'newsletter', count: 10000 },
  { label: 'VIP Members', value: 'vip', count: 1000 },
  { label: 'First-time Visitors', value: 'first_time', count: 3000 }
];

export const DEFAULT_SPLIT_CONFIG: NonNullable<AudienceFormValues['splitConfig']> = {
  isEnabled: false,
  splitType: 'even',
  variants: []
};
